import { useCallback, useEffect } from 'react';

import { isValidDirectionChange } from '@/features/letterParty/helpers';

import type { RefObject } from 'react';
import type { Direction } from '@/features/letterParty/types';

interface UseGameControlsProps {
	gameStatus: 'playing' | 'won' | 'completed' | 'gameOver';
	waitingForFirstDirection: boolean;
	isRecovering: boolean;
	isStopped: boolean;
	directionRef: RefObject<Direction>;
	onFirstDirectionChosen: () => void;
	onResume: () => void;
	onDirectionChange: (newDirection: Direction, previousDirection: Direction) => void;
}

/**
 * Custom hook to handle game controls
 *
 * Handles:
 * - Keyboard arrow key input
 * - Direction validation (prevents 180-degree turns)
 * - First direction waiting state
 * - Resume from stopped state
 *
 * @param gameStatus - Current game status
 * @param waitingForFirstDirection - If true, waiting for player to choose initial direction
 * @param isRecovering - If true, game is paused during recovery
 * @param isStopped - If true, snake is stopped waiting for input
 * @param directionRef - Current direction reference
 * @param onFirstDirectionChosen - Callback when first direction is chosen
 * @param onResume - Callback to resume game from stopped state
 * @param onDirectionChange - Callback to move immediately after a valid direction change
 * @returns Direction change handler for non-keyboard input
 */
export function useGameControls({
	gameStatus,
	waitingForFirstDirection,
	isRecovering,
	isStopped,
	directionRef,
	onFirstDirectionChosen,
	onResume,
	onDirectionChange,
}: UseGameControlsProps) {
	/**
	 * Apply a direction change with validation
	 */
	const applyDirectionChange = useCallback(
		(newDirection: Direction) => {
			if (gameStatus !== 'playing' || isRecovering) {
				return;
			}

			const currentDirection = directionRef.current;

			// Validate direction change
			if (!isValidDirectionChange(currentDirection, newDirection)) {
				return;
			}

			// Handle first direction choice
			if (waitingForFirstDirection) {
				onFirstDirectionChosen();
				onDirectionChange(newDirection, currentDirection);
				return;
			}

			// Resume if stopped
			if (isStopped) {
				onResume();
			}

			if (currentDirection !== newDirection || isStopped) {
				onDirectionChange(newDirection, currentDirection);
			}
		},
		[
			directionRef,
			gameStatus,
			isRecovering,
			isStopped,
			onDirectionChange,
			onFirstDirectionChosen,
			onResume,
			waitingForFirstDirection,
		],
	);

	// Keyboard controls
	useEffect(() => {
		if (gameStatus !== 'playing') return;
		if (isRecovering) return; // Don't accept input during recovery

		const handleKeyPress = (e: KeyboardEvent) => {
			let newDirection: Direction | null = null;

			switch (e.key) {
				case 'ArrowUp':
					newDirection = 'UP';
					e.preventDefault();
					break;
				case 'ArrowDown':
					newDirection = 'DOWN';
					e.preventDefault();
					break;
				case 'ArrowLeft':
					newDirection = 'LEFT';
					e.preventDefault();
					break;
				case 'ArrowRight':
					newDirection = 'RIGHT';
					e.preventDefault();
					break;
			}

			if (newDirection) {
				applyDirectionChange(newDirection);
			}
		};

		window.addEventListener('keydown', handleKeyPress);
		return () => window.removeEventListener('keydown', handleKeyPress);
	}, [gameStatus, isRecovering, applyDirectionChange]);

	return applyDirectionChange;
}
